import React, { useEffect, useState } from "react";
import { useSnackbar } from "notistack";
import { Dialog, Button, DialogActions, DialogContent, DialogTitle, LinearProgress, Typography } from "@material-ui/core";
import { ClubSelect, PlainTextField } from "components";
import { Club, updateCompanyMemberBudget } from "api/clubs";
import { getMembers } from "api/admin/members";
import CurrencyTextField from "@unicef/material-ui-currency-textfield";
import i18n from "./i18n";
import FirstWarningMsgDialog from "./FirstWarningMsgDialog";
import WarningMsgDialog from "./WarningMsgDialog";

type MembersBulkEditDialogProps = {
    showDialog: boolean,
    onClose: () => void,
    onRefreshMembersTable: () => void,
    club: Club
};

const MembersBulkEditDialog = ({ showDialog, onClose, onRefreshMembersTable, club }: MembersBulkEditDialogProps) => {
    // snackbar
    const { enqueueSnackbar } = useSnackbar();
    const displayError = (error: any) => enqueueSnackbar(
        `${i18n.anErrorOccurred}: ${error.description} (${error.type})`,
        { variant: "error" },
    );
    const [clubId, setClubId] = useState<string | undefined>();
    const [query, setQuery] = useState("");
    const [showLoadingAnimation, setShowLoadingAnimation] = useState(false);
    const [affectedEntries, setAffectedEntries] = useState(0);
    const [monthlyCompanyMemberBudget, setMonthlyCompanyMemberBudget] = useState(0);
    const [showWarningDialog, setWarningDialog] = useState(false);
    const [isChangedMonthlyBudget, setIsChangedMonthlyBudget] = useState(false);
    const [applyToCurrentMonth, setApplyToCurrentMonth] = useState(false);
    const [showFirstWarningDialog, setShowFirstWarningDialog] = useState(false);

    useEffect(() => {
        getMembers({ clubId: clubId || club.id, query }).then(({ totalCount }) => setAffectedEntries(totalCount));
    }, [clubId, query, club]);

    const preSaveAction = () => {
        if (isChangedMonthlyBudget) {
            setWarningDialog(false);
            setShowFirstWarningDialog(true);
        } else {
            setApplyToCurrentMonth(false);
            executeAction(true, false);
        }
    };

    const firstWarningAction = async (applyToCurrentMonthFlag: boolean) => {
        setApplyToCurrentMonth(applyToCurrentMonthFlag);
        setShowFirstWarningDialog(false);
        setWarningDialog(true);
    };

    const executeAction = async (applySaveAction: boolean, applyToCurrentMonthFlag: boolean) => {
        if (!applySaveAction) {
            setWarningDialog(false);
        } else {
            setShowLoadingAnimation(true);
            try {
                // bulk update company member budget
                await updateCompanyMemberBudget({
                    clubId: (clubId === undefined ? club.id : clubId),
                    monthlyCompanyMemberBudgetCents: (monthlyCompanyMemberBudget * 100),
                    applyToCurrentMonth: applyToCurrentMonthFlag,
                    query,
                });
                enqueueSnackbar(i18n.savedSuccessfully, { variant: "success" });
                onRefreshMembersTable(); // fetch update data
                onClose();
            } catch (error) {
                displayError(error);
            } finally {
                setShowLoadingAnimation(false);
                setWarningDialog(false);
            }
        }
    };

    // render
    return (
        <>
            {/* Worning message */}
            <FirstWarningMsgDialog showFirstWarningDialog={showFirstWarningDialog} onCloseFirstDialog={() => setShowFirstWarningDialog(false)} firstWarningAction={firstWarningAction} />
            <WarningMsgDialog show={showWarningDialog} applyToCurrentMonth={applyToCurrentMonth} onClose={() => setWarningDialog(false)} saveAction={executeAction} />
            <Dialog
                fullWidth
                open={showDialog}
                onClose={onClose}
            >
                <DialogTitle>{i18n.bulkEdit}</DialogTitle>
                <DialogContent dividers>

                    {/* club */}
                    <ClubSelect
                        customLabel={i18n.organisation}
                        clubId={clubId || club.id}
                        controlProps={{ fullWidth: true, margin: "normal" }}
                        userRoles={["owner"]}
                        onChange={setClubId}
                    />

                    {/* query */}
                    <PlainTextField
                        label={i18n.query}
                        value={query}
                        variant="standard"
                        onChange={(e) => setQuery(e.target.value)}
                    />

                    {/* affected entries */}
                    <Typography align="right" variant="subtitle2">
                        {i18n.affectedEntries(affectedEntries)}
                    </Typography>

                    {/* Monthly company member budget */}
                    <CurrencyTextField
                        currencySymbol="€"
                        decimalCharacter=","
                        digitGroupSeparator="."
                        fullWidth
                        label={i18n.monthlyCompanyMemberBudgetCents}
                        helperText=""
                        maximumValue="1000000"
                        minimumValue="0"
                        value={0}
                        variant="outlined"
                        onChange={(e: any, value: number) => { setMonthlyCompanyMemberBudget(value); setIsChangedMonthlyBudget(true); }}
                    />
                </DialogContent>

                {showLoadingAnimation && (
                    <LinearProgress />
                )}

                <DialogActions>
                    <Button onClick={onClose} color="secondary">
                        {i18n.cancel}
                    </Button>
                    <Button disabled={affectedEntries === 0} onClick={() => preSaveAction()} color="primary">
                        {i18n.execute}
                    </Button>
                </DialogActions>
            </Dialog>
        </>
    );
};

export default MembersBulkEditDialog;
